import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { LocationStatus, LOCATION_STATUS_STYLES } from "../../../../types/LocationStatus";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Color variant for an individual status tile.
 * Alias of {@link LocationStatus} — the shared token map drives all coloring.
 */
export type WorkflowStepVariant = LocationStatus;

/**
 * A single action available to a location in a given status (e.g. "Edit location details").
 */
export interface WorkflowStepAction {
  /** Stable unique identifier used as the React list key. */
  id: string;
  /** Leading icon. Typically, an MUI icon component (e.g. `<EditRounded fontSize="small" />`). */
  icon: React.ReactNode;
  /** Short action description. */
  text: string;
}

/**
 * A single status tile in the reference grid.
 */
export interface WorkflowStep {
  /** Stable unique identifier used as the React list key. */
  id: string;
  /** Color theme for the tile, bubble, and name. */
  variant: WorkflowStepVariant;
  /**
   * Icon rendered inside the status bubble.
   * Typically, an MUI icon component (e.g., `<EditRounded fontSize="small" />`).
   */
  icon: React.ReactNode;
  /** Status label rendered beside the bubble (e.g. "Active"). */
  label: string;
  /** One-line description of what the status means. */
  description: string;
  /** Optional list of actions available to a location in this status. */
  actions?: WorkflowStepAction[];
}

/**
 * Props for the {@link LocationApprovalWorkflow} component.
 */
export interface LocationApprovalWorkflowProps {
  /**
   * Icon rendered beside the section header label.
   * Typically, an MUI icon component (e.g., `<GridViewRounded fontSize="inherit" />`).
   */
  headerIcon?: React.ReactNode;
  /**
   * Section header label text. Defaults to `"Location Statuses"`.
   */
  title?: string;
  /** Status tiles to display, in the order they should appear. */
  steps: WorkflowStep[];
}

// ─── Variant token maps ───────────────────────────────────────────────────────

/**
 * Re-exported alias of {@link LOCATION_STATUS_STYLES} for use within this component.
 * Consuming code should import directly from `types/LocationStatus` when needed outside the grid.
 */
const STEP_STYLES = LOCATION_STATUS_STYLES;

// ─── Styled primitives ────────────────────────────────────────────────────────

/** Outer card container. Full-width within its parent grid cell. */
const WorkflowRoot = styled(Box)({
  backgroundColor: "#f8fafc",
  border: "1px solid #e8eef4",
  borderRadius: "6px",
  padding: "16px 20px",
});

/** Section header label: small, uppercase, gray. */
const WorkflowLabel = styled(Box)({
  fontSize: "0.7143rem",       // 10px
  fontWeight: 700,
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  color: "#5E7782",
  marginBottom: "14px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
});

/**
 * Responsive tile grid: 4 columns on large screens, 2 on medium, 1 on small —
 * so no tile is ever cramped.
 */
const StatusGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "12px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

/** Individual status tile. Variant-specific bg + border applied from the shared tokens. */
const StatusTile = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerVariant",
})<{ ownerVariant: WorkflowStepVariant }>(({ ownerVariant }) => ({
  borderRadius: "6px",
  padding: "14px 14px 12px",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  border: `1px solid ${STEP_STYLES[ownerVariant].border}`,
  backgroundColor: STEP_STYLES[ownerVariant].bg,
}));

/** Tile header row: bubble + status name. */
const StatusTileHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

/** Circular status bubble. */
const StatusBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerVariant",
})<{ ownerVariant: WorkflowStepVariant }>(({ ownerVariant }) => ({
  width: 30,
  height: 30,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  backgroundColor: STEP_STYLES[ownerVariant].border,
  color: STEP_STYLES[ownerVariant].color,
  fontSize: "0.9286rem",   // 13px
}));

/** Status name (e.g. "Active"). */
const StatusName = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerVariant",
})<{ ownerVariant: WorkflowStepVariant }>(({ ownerVariant }) => ({
  fontSize: "0.9286rem",   // 13px
  fontWeight: 700,
  color: STEP_STYLES[ownerVariant].color,
}));

/** Status description text. */
const StatusDesc = styled(Box)({
  fontSize: "0.8214rem",   // 11.5px
  color: "#5a7080",
  lineHeight: 1.45,
});

/** Footer list of available actions, separated by a hairline rule. */
const StatusActions = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "1px",
  marginTop: "2px",
  paddingTop: "8px",
  borderTop: "1px solid rgba(0,0,0,0.06)",
});

/** Single action row: leading icon + text. */
const StatusActionItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  fontSize: "0.7857rem",   // 11px
  fontWeight: 600,
  color: "#5a7080",
  gap: "5px",
  "& svg": {
    marginTop: "1px",
    flexShrink: 0,
    fontSize: "0.95rem",   // 11px
  },
});

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Status reference grid that explains the location approval lifecycle.
 *
 * Renders one tile per {@link LocationStatus} (Active, Pending, Draft, Deactivated),
 * each showing a colored bubble, a name, a short description, and the actions
 * available to a location in that status. The grid reflows from 4 → 2 → 1 column
 * as the viewport narrows.
 *
 * Variant token maps ({@link LOCATION_STATUS_STYLES}) drive all colors, so the tiles
 * stay in sync with the card borders, ribbons, and tab badges elsewhere in the app.
 *
 * @example
 * <LocationApprovalWorkflow
 *   headerIcon={<GridViewRounded fontSize="inherit" />}
 *   title="Location Statuses"
 *   steps={[
 *     {
 *       id: "active",
 *       variant: "active",
 *       icon: <CheckRounded fontSize="small" />,
 *       label: "Active",
 *       description: "Approved and live. Buyers can find and book your space.",
 *       actions: [
 *         { id: "edit", icon: <EditRounded fontSize="small" />, text: "Edit location details" },
 *         { id: "deactivate", icon: <BlockRounded fontSize="small" />, text: "Deactivate to take offline" },
 *       ],
 *     },
 *     // ...pending, draft, deactivated
 *   ]}
 * />
 */
const LocationApprovalWorkflow = ({
  headerIcon,
  title = "Location Statuses",
  steps,
}: LocationApprovalWorkflowProps) => {
  return (
    <WorkflowRoot>
      <WorkflowLabel>
        {headerIcon && <Box component="span" aria-hidden="true">{headerIcon}</Box>}
        {title}
      </WorkflowLabel>

      <StatusGrid>
        {steps.map((step) => (
          <StatusTile key={step.id} ownerVariant={step.variant} aria-label={`${step.label} status`}>
            <StatusTileHeader>
              <StatusBubble ownerVariant={step.variant} aria-hidden="true">
                {step.icon}
              </StatusBubble>
              <StatusName ownerVariant={step.variant}>{step.label}</StatusName>
            </StatusTileHeader>

            <StatusDesc>{step.description}</StatusDesc>

            {step.actions && step.actions.length > 0 && (
              <StatusActions>
                {step.actions.map((action) => (
                  <StatusActionItem key={action.id}>
                    <Box component="span" aria-hidden="true" sx={{ display: "inline-flex" }}>
                      {action.icon}
                    </Box>
                    {action.text}
                  </StatusActionItem>
                ))}
              </StatusActions>
            )}
          </StatusTile>
        ))}
      </StatusGrid>
    </WorkflowRoot>
  );
};

export default LocationApprovalWorkflow;
